import { FC, useEffect, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import type { MatrixThresholds } from "@/lib/scoring/matrix/bands";

interface ThresholdControlsProps {
  value: MatrixThresholds;
  onChange: (t: MatrixThresholds) => void;
}

const DEBOUNCE_MS = 500;

function clampScore(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

const NumberField: FC<{
  label: string;
  value: number;
  testId: string;
  onChange: (n: number) => void;
}> = ({ label, value, testId, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <TextField
      type="number"
      size="small"
      value={value}
      onChange={(e) => onChange(clampScore(parseInt(e.target.value, 10)))}
      inputProps={{
        min: 0,
        max: 100,
        "data-testid": testId,
        style: { width: 48, padding: "4px 6px", textAlign: "center" },
      }}
    />
  </Stack>
);

/**
 * "High ≥ [n]   Mid ≥ [n]" controls. Edits are local and debounced before
 * committing, so the matrix's per-cell queries don't refire on every keystroke.
 */
export const ThresholdControls: FC<ThresholdControlsProps> = ({
  value,
  onChange,
}) => {
  const [local, setLocal] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Keep local in sync if the source value changes externally.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (next: MatrixThresholds) => {
    setLocal(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(next), DEBOUNCE_MS);
  };

  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <NumberField
        label="High ≥"
        value={local.high}
        testId="matrix-threshold-high"
        onChange={(high) => commit({ ...local, high })}
      />
      <NumberField
        label="Mid ≥"
        value={local.mid}
        testId="matrix-threshold-mid"
        onChange={(mid) => commit({ ...local, mid })}
      />
    </Stack>
  );
};
